import { Component, computed, inject, input, Input, OnInit, signal, TemplateRef, ViewChild } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import {
  AccountMarketplace,
  CampaignType,
  MatchType,
  StrategyStateEnum,
  StrategyType,
  TacticType,
} from "@front/m19-api-client";
import { StrategyEx, StrategyGroupEx, StrategyTargetingType, StrategyTypeStr } from "@front/m19-models";
import { AccountSelectionService, AsinService, SegmentService, StrategyService } from "@front/m19-services";
import { ConfirmPopupComponent, IButtonComponent, ModalRef, ModalService, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AsinSelectionWrapperComponent } from "@m19-board/shared/asin-selection/asin-selection-wrapper.component";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import { TargetingAsinStatsComponent } from "@m19-board/sponsored-product/sp-page/targeting-asin-stats/targeting-asin-stats.component";
import { StrategyPageComponent } from "@m19-board/strategies/strategy-page/strategy-page.component";
import { TargetingLimit } from "@m19-board/strategies/target-utils";
import { ICON_WARNING_TRIANGLE } from "@m19-board/utils/iconsLabels";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, forkJoin, switchMap } from "rxjs";
import {
  StrategyGroupMoveProductModalComponent,
  StrategyGroupMoveProductModalData,
  TargetProductStrategy,
} from "./move-product/strategy-group-move-product-modal.component";
@UntilDestroy()
@Component({
  selector: "app-sp-substrategy",
  templateUrl: "./sp-substrategy.component.html",
  styleUrls: ["./sp-substrategy-creation/sp-substrategy-creation.component.scss"],
  standalone: true,
  imports: [
    TranslocoDirective,
    StrategyPageComponent,
    TargetingAsinStatsComponent,
    IButtonComponent,
    AsinSelectionComponent,
    AsinSelectionWrapperComponent,
  ],
})
export class SpSubStrategyComponent implements OnInit {
  strategy = input.required<StrategyEx>();
  strategy$ = toObservable(this.strategy);

  @Input()
  strategyGroup!: StrategyGroupEx;

  productTargetings = computed(() =>
    this.strategy()
      .targetings.filter((t) => t.matchType == MatchType.asinSameAs)
      .map((p) => ({
        asin: p.targetingValue,
      })),
  );

  strategyWarnings = signal<string[]>([]);

  accountMarketplace: AccountMarketplace | undefined;
  isReadOnly = false;
  asinEligibility: Map<string, { status: boolean; reason: string }> = new Map();
  strategyTypeStr!: string;
  strategyType!: StrategyType;
  withTacticSection!: boolean;
  withTOSROSwitch!: boolean;
  withAutoCampaignOption!: boolean;
  withProductTargetingOption!: boolean;
  productTargetingAllowAsinFromOtherCatalog!: boolean;
  strategyTargetingType!: StrategyTargetingType;
  asinsCanBeMovedToOtherStrat!: boolean;

  addAsinList = signal<string[]>([]);
  selectedAsins: string[] = [];
  strategyAsins = computed(() => new Set(this.strategy().asins.map((a) => a.asin!)));

  @ViewChild("productAdd") productAddModal!: TemplateRef<any>;
  addAsinModalRef?: ModalRef<any>;

  modalRef = inject(ModalRef, { optional: true });

  readonly State = StrategyStateEnum;
  readonly sectionVisibility = {
    products: true,
    targetingStats: false,
  };

  protected readonly StrategyType = StrategyType;
  protected readonly CampaignType = CampaignType;

  constructor(
    private asinService: AsinService,
    private toastrService: ToasterService,
    private accountMarketplaceSelection: AccountSelectionService,
    private modalService: ModalService,
    private segmentService: SegmentService,
    private strategyService: StrategyService,
    private translocoService: TranslocoService,
  ) {
    this.accountMarketplaceSelection.singleAccountMarketplaceSelection$.pipe(untilDestroyed(this)).subscribe((am) => {
      this.accountMarketplace = am;
    });
  }

  ngOnInit(): void {
    this.accountMarketplaceSelection.readOnlyMode$.pipe(untilDestroyed(this)).subscribe((b) => (this.isReadOnly = b));
    this.asinService
      .getCatalog(this.strategyGroup.accountId!, this.strategyGroup.marketplace!)
      .pipe(untilDestroyed(this))
      .subscribe((catalog) => {
        this.asinEligibility = catalog.getSPEligibility();
      });
    this.strategyTypeStr = StrategyTypeStr[this.strategy().strategyType];
    this.strategyType = this.strategy().strategyType;
    this.withTacticSection = this.strategy().strategyType == StrategyType.PRODUCT;
    this.withAutoCampaignOption = this.strategy().strategyType == StrategyType.PRODUCT;
    this.withProductTargetingOption = this.strategy().strategyType == StrategyType.PRODUCT;
    this.withTOSROSwitch = this.strategy().strategyType == StrategyType.PRODUCT;

    if (this.strategy().disableOtherQueries) {
      this.strategyWarnings.update((warnings) => [
        ...warnings,
        this.translocoService.translate("strategy-info-cell.ai-powered_targeting_is_deactivated"),
      ]);
    }
    this.asinsCanBeMovedToOtherStrat = this.strategy().strategyType == StrategyType.PRODUCT;

    if (this.strategy().targetings.length > 0 && this.strategy().targetings[0].matchType == MatchType.asinSameAs) {
      this.strategyTargetingType = StrategyTargetingType.PRODUCTS;
    } else {
      this.strategyTargetingType = StrategyTargetingType.KEYWORDS;
    }
    this.productTargetingAllowAsinFromOtherCatalog = this.strategyType != StrategyType.BRAND;
    combineLatest([
      this.strategy$,
      this.accountMarketplaceSelection.singleAccountMarketplaceSelection$.pipe(
        switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
      ),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([strategy, segmentIndex]) => {
        const segments = strategy?.tactics
          .filter((tactic) => tactic.tacticType == TacticType.LEGACY)
          .map((tactic) => segmentIndex.get(tactic.segmentId!)!);
        const blacklist = strategy?.tactics
          .filter((tactic) => tactic.tacticType == TacticType.BLACKLIST)
          .map((tactic) => segmentIndex.get(tactic.segmentId!)!);
        const targetLimit = new TargetingLimit(segments ?? [], blacklist ?? []);
        if (targetLimit.tooManyTargets()) {
          this.strategyWarnings.update((warnings) => [...warnings, targetLimit.formatErrorMessage()]);
        }
      });
  }

  close() {
    this.modalRef?.close();
  }

  showAddAsinModal() {
    this.addAsinModalRef = this.modalService.openModal(this.productAddModal, {
      modalTitle: this.translocoService.translate("sp-substrategy.add_products"),
    });
  }

  toggleSectionVisibility(section: keyof typeof this.sectionVisibility) {
    this.sectionVisibility[section] = !this.sectionVisibility[section];
  }

  addProducts(toAdd: string[]) {
    this.strategyService.addAsinsToStrategy(this.strategy(), toAdd).subscribe({
      next: () => {
        this.toastrService.success(
          this.translocoService.translate("sp-substrategy.asin_s_added_to_strategy"),
          this.translocoService.translate("sp-strategy-group-page.strategy_asin_updated"),
        );
        this.addAsinModalRef?.close();
        this.addAsinList.set([]);
      },
      error: (error: string) => {
        this.toastrService.error(
          this.translocoService.translate("sp-substrategy.error_adding_strategy_asin_s_error", [error]),
          this.translocoService.translate("sp-strategy-group-page.strategy_asin_update_error"),
        );
      },
    });
  }

  removeProducts(toRemove: string[]) {
    if (this.strategyType != StrategyType.PRODUCT) {
      this.deleteProducts(toRemove);
      return;
    }
    // for product strategy deleting products might impact other strategies

    // for product strategy deleting products might impact other strategies
    const toRemoveAsins = toRemove;
    const toRemoveSet = new Set<string>(toRemoveAsins);
    const impactedStrats = new Map<StrategyEx, string[]>();
    for (const otherStrat of this.strategyGroup.brandStrategies.concat(this.strategyGroup.keywordStrategies)) {
      const intersection = otherStrat.asins.map((x) => x.asin).filter((a) => toRemoveSet.has(a));
      if (intersection.length > 0) {
        impactedStrats.set(otherStrat, intersection);
      }
    }
    if (impactedStrats.size == 0) {
      return this.deleteProducts(toRemoveAsins);
    }
    const modalRef = this.modalService.openModal(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("sp-strategy-group-page.removing_asins_from_main_strategy"),
      data: {
        message: `These ASINs are used in ${impactedStrats.size} brand defense or focus ${
          impactedStrats.size > 1 ? "strategies" : "strategy"
        } (${Array.from(impactedStrats.keys())
          .map((s) => `"${s.name}"`)
          .join(", ")}). They will stop running for these products.`,
      },
    });
    return modalRef
      .afterClosed()
      .pipe(
        switchMap(() =>
          forkJoin(
            Array.from(impactedStrats.entries()).map(([strat, asins]) =>
              this.strategyService.deleteAsinsFromStrategy(strat, asins),
            ),
          ),
        ),
        switchMap(() => this.strategyService.deleteAsinsFromStrategy(this.strategy(), toRemoveAsins)),
        // TODO: also stop strategies if no more ASINs
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("sp-strategy-group-page.asin_removed_from_strategy"),
            this.translocoService.translate("sp-strategy-group-page.strategy_asin_updated"),
          );
        },
        error: (error: string) => {
          this.toastrService.error(
            this.translocoService.translate("sp-strategy-group-page.error_removing_strategy_asin_s_error", [error]),
            this.translocoService.translate("sp-strategy-group-page.strategy_asin_update_error"),
          );
        },
      });
  }

  private deleteProducts(toRemove: string[]) {
    this.strategyService.deleteAsinsFromStrategy(this.strategy(), toRemove).subscribe({
      next: () => {
        this.toastrService.success(
          this.translocoService.translate("sp-strategy-group-page.asin_removed_from_strategy"),
          this.translocoService.translate("sp-strategy-group-page.strategy_asin_updated"),
        );
        this.selectedAsins = [];
      },
      error: (error: string) => {
        this.toastrService.error(
          this.translocoService.translate("sp-strategy-group-page.error_removing_strategy_asin_s_error", [error]),
          this.translocoService.translate("sp-strategy-group-page.strategy_asin_update_error"),
        );
      },
    });
  }

  moveToOtherStrategy(asins: string[]) {
    if (!this.accountMarketplace) {
      return;
    }
    // cannot move all ASINs to a new strategy
    if (asins.length == this.strategy().asins.length) {
      this.toastrService.error("You cannot move all ASINs to a new strategy");
      return;
    }
    this.modalService.openModal<StrategyGroupMoveProductModalData, TargetProductStrategy>(
      StrategyGroupMoveProductModalComponent,
      {
        modalTitle: this.translocoService.translate(
          "strategy-group-move-product-modal.move_products_to_another_main_strategy",
        ),
        data: {
          strategyGroup: this.strategyGroup,
          source: this.strategy(),
          asinsToMove: asins,
        },
      },
    );
  }

  protected readonly ICON_WARNING_TRIANGLE = ICON_WARNING_TRIANGLE;
}
